/**
 * Currency-aware money handling, shared by the Worker and both clients.
 *
 * The one rule worth stating up front: **a minor unit is not always a hundredth**. ISO 4217
 * gives each currency an exponent — 2 for USD and EUR, 0 for JPY, KRW, ISK, CLP and VND,
 * 3 for BHD and KWD. Code that multiplies by 100 to "convert to cents" is correct for the
 * currencies its author happened to test with and silently wrong by a factor of 100 for
 * the rest, which is how ¥5,000 becomes ¥500,000.
 *
 * The exponent comes from ICU rather than a table kept here, so it stays right without
 * anyone maintaining it. Both the Workers runtime and every browser/RN engine we target
 * ship full ICU; the fallbacks below cover a malformed code rather than a missing one.
 */

/** ISO 4217's exponent for a currency: how many decimal places it actually has. */
export function currencyExponent(currency: string): number {
  try {
    // Typed as optional because the field is absent for non-currency formats; a currency
    // format always resolves it.
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    // Malformed code (not three letters). An unknown-but-well-formed one resolves to 2,
    // which is the right guess anyway.
    return 2;
  }
}

/**
 * Converts a provider's decimal amount into minor units.
 *
 * RevenueCat reports `price` in major units (39.99, or 5000 for ¥5,000), so the exponent
 * decides the multiplier. A null currency falls back to two places, matching the old
 * behaviour for rows written before the currency was recorded.
 */
export function toMinorUnits(amount: number, currency: string | null): number {
  const exponent = currency == null ? 2 : currencyExponent(currency);
  return Math.round(amount * 10 ** exponent);
}

/** The inverse: minor units back to the major-unit amount used for display. */
export function fromMinorUnits(amountMinor: number, currency: string | null): number {
  const exponent = currency == null ? 2 : currencyExponent(currency);
  return amountMinor / 10 ** exponent;
}

/**
 * Guarantees a price carries its ISO code, e.g. `$54.99` → `$54.99 CAD`.
 *
 * A bare `$54.99` reads as US dollars to most of the world, and Canadian, Australian,
 * Singaporean and Hong Kong customers all see a plain `$` in their own locale. The symbol
 * alone therefore cannot say what was charged, so the code always rides along.
 *
 * Idempotent: a string already naming its currency — `CAD 54.99`, `54,99 EUR` — is left
 * as it is rather than growing a second copy.
 */
export function withCurrencyCode(formatted: string, currency: string | null): string {
  if (currency == null || currency === '') return formatted;
  const code = currency.toUpperCase();
  if (new RegExp(`(^|[^A-Z])${code}([^A-Z]|$)`, 'i').test(formatted)) return formatted;
  return `${formatted} ${code}`;
}

/**
 * A localised price string that always names its currency: `$39.99 USD`, `¥5,000 JPY`.
 *
 * Only for amounts we hold ourselves — what a household was actually charged, read back
 * from the `subscription` row. A price the customer has *not* yet paid should come from
 * the store SDK's own formatted string instead (passed through `withCurrencyCode`), so
 * what we show and what they are charged cannot disagree.
 *
 * `narrowSymbol` keeps the symbol short precisely because the code follows it: the wider
 * `CA$54.99 CAD` says the same thing twice.
 */
export function formatMoney(
  amountMinor: number,
  currency: string,
  locale?: string,
): string {
  const amount = fromMinorUnits(amountMinor, currency);
  try {
    return withCurrencyCode(
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount),
      currency,
    );
  } catch {
    // An unresolvable code cannot produce a symbol, so the code alone does the work.
    return `${amount.toFixed(currencyExponent(currency))} ${currency}`;
  }
}
